/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../auth';
import queryString from 'querystring';
import { useRouter } from 'src/useRouter';
import { LoadingScreen } from '../LoadingScreen';
import { FirebaseLoginWidget } from './FirebaseLoginWidget';
import { HandleLoginSuccess } from 'src/HandleLoginSuccess/HandleLoginSuccess';
import { Wordmark } from '../Marketing/components/Wordmark';

export const Login = ({
  shouldRedirectToWhereTheUserWas = true,
}: {
  shouldRedirectToWhereTheUserWas: boolean;
}) => {
  const route = useRouter();
  const { user, initializing, error } = useAuthContext();
  const whereTheUserWasBeforeLoggingIn = useMemo(() => {
    const params = queryString.parse(route.location.search.slice(1));
    return typeof params.postLoginRedirect === 'string'
      ? params.postLoginRedirect
      : null;
  }, [route.location.search]);

  if (initializing) {
    console.log('Login - initializing');
    return <LoadingScreen />;
  }
  if (error) {
    console.log('Login - error');
    return (
      <div>
        <p>Error: {error}</p>
      </div>
    );
  }
  if (user) {
    console.log('Login - have user, redirect?');
    if (!shouldRedirectToWhereTheUserWas) {
      return null;
    }

    return (
      <HandleLoginSuccess
        whereTheUserWasBeforeLoggingIn={whereTheUserWasBeforeLoggingIn}
      />
    );
  }

  console.log('Login - no user found?');

  return (
    <div
      css={css`
        display: flex;
        width: 100%;
        height: 100%;
        min-height: 100vh;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: linear-gradient(
          to bottom right,
          rgba(239, 246, 255, 0.4),
          white,
          rgba(255, 241, 242, 0.3)
        );
      `}
    >
      {/* Wordmark */}
      <div
        className="animate-fade-in-up"
        css={css`
          margin-bottom: 2rem;
          opacity: 0;
          animation: fade-in-up 0.5s ease-out forwards;
        `}
      >
        <Wordmark className="h-10 w-auto" />
      </div>

      {/* Welcome text */}
      <p
        css={css`
          color: #6b7280;
          font-size: 0.875rem;
          margin-bottom: 2rem;
          opacity: 0;
          animation: fade-in-up 0.5s ease-out forwards;
          animation-delay: 0.1s;
        `}
      >
        Sign in to continue
      </p>

      {/* Auth buttons */}
      <div
        css={css`
          opacity: 0;
          animation: fade-in-up 0.5s ease-out forwards;
          animation-delay: 0.2s;

          /* Override FirebaseUI email button color */
          .firebaseui-idp-password {
            background-color: #111827 !important;
          }
        `}
      >
        <FirebaseLoginWidget />
      </div>

      {/* Back link */}
      <Link
        to="/"
        css={css`
          margin-top: 3rem;
          font-size: 0.875rem;
          color: #9ca3af;
          text-decoration: none;
          transition: color 0.2s;
          opacity: 0;
          animation: fade-in-up 0.5s ease-out forwards;
          animation-delay: 0.3s;

          &:hover {
            color: #4b5563;
          }
        `}
      >
        ← Back to home
      </Link>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
